import { useCallback, useEffect, useMemo, useState } from 'react';
import './worldShell.css';
import SetupScreen from './SetupScreen.js';
import CharactersScreen from './CharactersScreen.js';
import CastScreen from './CastScreen.js';
import GameScreen from './GameScreen.js';
import LlmMeter from './LlmMeter.js';
import CreateWorldWizard from './CreateWorld/CreateWorldWizard.js';
import { loadDraftFromStorage, loadLaunchHandoff, saveDraftToStorage } from './CreateWorld/draftStorage.js';
import {
  bodiesForAddCharacter,
  commitCharactersToLocation,
  draftForAddCharacter,
} from './CreateWorld/locationCharacterReturn.js';
import type { CharacterBodySnapshot } from './CreateWorld/characterBody.js';
import { ensureBundledWorld } from './bundledWorld.js';
import { prepareAquariumEntry } from './worldRuntime/launchMode.js';
import {
  clearPersistedKey,
  clearPersistedKeys,
  loadPersistedKey,
  loadPersistedKeys,
  loadPrefs,
  savePersistedKey,
  savePersistedKeys,
  savePrefs,
} from './secureStore.js';
import {
  cloneSavedForWorld,
  getWorldLibrary,
  loadLibrary,
  openWorldLibrary,
  resetWorldLibrary,
  WORLD_LIBRARY_EMPTY_BODY,
  WORLD_LIBRARY_EMPTY_TITLE,
  WORLD_LIBRARY_LEGACY_UNMIGRATABLE,
} from './library.js';
import type { WorldLibrarySort, WorldListItem } from '../db/worldLibrary.js';
import { DEFAULT_SCENARIO_ID, scenarioById } from '../sim/scenarios.js';
import type { Npc } from '../types.js';
import type { LlmProvider } from '../llm/client.js';
import { isHostedSite } from '../llm/hosted.js';
import type { WorldDraft } from '../domain/types.js';

export type Screen = 'menu' | 'load-world' | 'setup' | 'characters' | 'cast' | 'game' | 'create-world';

function providerStatus(hasKey: boolean, ready: boolean): string {
  if (!ready) return 'Loading saved credentials…';
  return hasKey
    ? 'DeepSeek is configured'
    : 'DeepSeek is not configured. Enter a key in Create World or Setup.';
}

function HomeShell(props: {
  hasKey: boolean;
  ready: boolean;
  onCreateWorld: () => void;
  onLoadWorld: () => void;
  onSetup: () => void;
}): JSX.Element {
  return (
    <div className="intro-screen">
      <div className="intro-art">
        <img src="/specimen-observer-aquarium.jpg" alt="" />
        <div className="intro-art-shade" />
        <div className="intro-title">
          <h1 className="game-title">SPECIMEN OBSERVER</h1>
          <div className="intro-credit intro-credit--spec">From openglass.games</div>
        </div>
      </div>
      <div className="intro-controls">
        <div className="subtitle">
          Create a place, choose a time, and populate it with characters. Watch the world advance while you are here and while you sleep.
        </div>
        <div className="menu-row">
          <button className="btn-primary" onClick={props.onCreateWorld}>Create World</button>
          <button className="btn-ghost" onClick={props.onLoadWorld}>Load World</button>
          {!isHostedSite() && (
            <button className="btn-ghost" onClick={props.onSetup}>Setup</button>
          )}
        </div>
        <div className="hint world-provider-hint">{providerStatus(props.hasKey, props.ready)}</div>
      </div>
    </div>
  );
}

function WorldLibraryScreen(props: {
  items: WorldListItem[];
  sort: WorldLibrarySort;
  legacyWarning: boolean;
  onSort: (sort: WorldLibrarySort) => void;
  onResume: (item: WorldListItem) => void;
  onDelete: (item: WorldListItem) => void;
  onCreateWorld: () => void;
  onBack: () => void;
}): JSX.Element {
  const sortButtons: { id: WorldLibrarySort; label: string }[] = [
    { id: 'recent', label: 'Recent' },
    { id: 'name', label: 'Name' },
    { id: 'created', label: 'Created' },
  ];

  return (
    <div className="world-library-screen screen-wide">
      <header className="world-library-head">
        <h1>Load World</h1>
        <div className="subtitle">All saved worlds and drafts</div>
      </header>
      {props.legacyWarning && (
        <p className="world-library-legacy" role="status">{WORLD_LIBRARY_LEGACY_UNMIGRATABLE}</p>
      )}
      <div className="world-library-sort">
        {sortButtons.map((b) => (
          <button
            key={b.id}
            type="button"
            className={`btn-ghost ${props.sort === b.id ? 'sel' : ''}`}
            onClick={() => props.onSort(b.id)}
          >
            {b.label}
          </button>
        ))}
      </div>
      {props.items.length === 0 ? (
        <div className="world-library-empty">
          <p><strong>{WORLD_LIBRARY_EMPTY_TITLE}</strong></p>
          <p className="hint">{WORLD_LIBRARY_EMPTY_BODY}</p>
          <button type="button" className="btn-primary" onClick={props.onCreateWorld}>Create World</button>
        </div>
      ) : (
        <div className="world-library-list">
          {props.items.map((item) => (
            <article key={`${item.recordType}:${item.id}`} className="world-library-item">
              <div className="world-library-item-head">
                <h2 className="world-library-item-title">{item.name}</h2>
                <span className="world-badge">{item.status}</span>
              </div>
              {item.subdescriptionSnippet && (
                <p className="world-library-snippet">{item.subdescriptionSnippet}</p>
              )}
              <p className="world-library-meta">
                {[item.regionCity, item.locationType].filter(Boolean).join(' · ')}
                {item.regionCity || item.locationType ? ' · ' : ''}
                Updated {new Date(item.updatedAt).toLocaleString()} · Step {item.progressStep} of {item.progressTotal}
              </p>
              <div className="world-library-actions">
                <button type="button" className="btn-primary" onClick={() => props.onResume(item)}>
                  {item.recordType === 'draft' ? 'Resume' : 'Open'}
                </button>
                {!isHostedSite() && (
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => props.onDelete(item)}
                  >
                    Delete
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
      <div className="menu-row" style={{ marginTop: 24 }}>
        <button type="button" className="btn-ghost" onClick={props.onBack}>Back</button>
      </div>
    </div>
  );
}

export default function App(): JSX.Element {
  const [screen, setScreen] = useState<Screen>('menu');
  const [npcCount, setNpcCount] = useState(6);
  const [roster, setRoster] = useState<Npc[]>([]);
  const [scenarioId, setScenarioId] = useState<string>(() => {
    try {
      return localStorage.getItem('converse:scenario') ?? DEFAULT_SCENARIO_ID;
    } catch {
      return DEFAULT_SCENARIO_ID;
    }
  });
  const [apiKeys, setApiKeys] = useState<string[]>([]);
  const [provider, setProvider] = useState<LlmProvider>('deepseek');
  const [model, setModel] = useState('deepseek-flash');
  const [linger, setLinger] = useState(0.75);
  const [ready, setReady] = useState(false);
  const [createDraft, setCreateDraft] = useState<WorldDraft | null>(null);
  const [addCharacterBodies, setAddCharacterBodies] = useState<Record<string, CharacterBodySnapshot> | null>(null);
  const [librarySort, setLibrarySort] = useState<WorldLibrarySort>('recent');
  const [libraryTick, setLibraryTick] = useState(0);
  const [legacyWarning, setLegacyWarning] = useState(false);

  const refreshKeys = useCallback(async () => {
    const prefs = loadPrefs();
    const [single, ring] = await Promise.all([
      loadPersistedKey(prefs.provider),
      loadPersistedKeys(prefs.provider),
    ]);
    setApiKeys(prefs.provider === 'gemini' ? ring : single ? [single] : []);
    setProvider(prefs.provider);
    setModel(prefs.model);
    setLinger(prefs.linger);
    setReady(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void ensureBundledWorld().then(() => {
      if (cancelled) return;
      resetWorldLibrary();
      bumpLibrary();
    });
    void refreshKeys().then(() => {
      if (cancelled) return;
    });
    return () => { cancelled = true; };
  }, [refreshKeys]);

  const library = useMemo(() => getWorldLibrary(), [libraryTick]);
  const libraryItems = useMemo(
    () => library.list(librarySort),
    [library, librarySort, libraryTick],
  );

  function bumpLibrary(): void {
    setLibraryTick((n) => n + 1);
  }

  function openLoadWorld(): void {
    const importResult = openWorldLibrary();
    setLegacyWarning(importResult.unmigratable || library.hasUnmigratableLegacy());
    setLibrarySort(library.getSortPreference());
    bumpLibrary();
    setScreen('load-world');
  }

  function handleLibrarySort(sort: WorldLibrarySort): void {
    library.setSortPreference(sort);
    setLibrarySort(sort);
    bumpLibrary();
  }

  function handleLibraryResume(item: WorldListItem): void {
    const step = library.resumeStep(item.id, item.recordType);
    if (!step) return;
    if (item.recordType === 'draft') {
      const resumed = library.resumeDraftRecord(item.id, item.resumeStep);
      if (!resumed) return;
      saveDraftToStorage(resumed.draft);
      setCreateDraft(resumed.draft);
      prepareAquariumEntry();
      setScreen('create-world');
      return;
    }
    const stored = library.getWorld(item.id);
    if (!stored) return;
    setRoster(stored.roster ?? []);
    setNpcCount(Math.min(20, Math.max(1, stored.roster?.length ?? 1)));
    prepareAquariumEntry();
    setScreen('game');
  }

  function handleLibraryDelete(item: WorldListItem): void {
    const label = item.recordType === 'draft' ? 'draft' : 'world';
    const ok = window.confirm(`Delete "${item.name}" (${label})? This cannot be undone.`);
    if (!ok) return;
    library.delete(item.id, item.recordType);
    bumpLibrary();
  }

  function startCreateWorld(): void {
    prepareAquariumEntry();
    setAddCharacterBodies(null);
    setCreateDraft(loadDraftFromStorage());
    setScreen('create-world');
  }

  function openAddCharacter(): void {
    const handoff = loadLaunchHandoff();
    if (!handoff) return;
    setAddCharacterBodies(bodiesForAddCharacter(handoff));
    setCreateDraft(draftForAddCharacter(handoff));
    setScreen('create-world');
  }

  function finishAddCharacter(draft: WorldDraft, bodies: Record<string, CharacterBodySnapshot>): void {
    const handoff = commitCharactersToLocation(draft, bodies);
    setAddCharacterBodies(null);
    setCreateDraft(null);
    if (handoff) {
      setRoster(handoff.roster);
      setNpcCount(Math.min(20, handoff.roster.length));
    }
    prepareAquariumEntry();
    setScreen('game');
  }

  if (screen === 'create-world') {
    return (
      <>
        <LlmMeter />
        <CreateWorldWizard
          initialDraft={createDraft}
          initialBodies={addCharacterBodies}
          onSavedToLocation={addCharacterBodies ? finishAddCharacter : undefined}
          onExit={() => { setAddCharacterBodies(null); setCreateDraft(null); setScreen('menu'); }}
          onOpenSetup={() => setScreen('setup')}
          onKeySaved={() => void refreshKeys()}
          onLaunch={(payload) => {
            getWorldLibrary().upsertWorld(payload.snapshot, 1, payload.roster);
            setRoster(payload.roster);
            setNpcCount(Math.min(20, payload.roster.length));
            setCreateDraft(null);
            setScreen('game');
          }}
        />
      </>
    );
  }
  if (screen === 'load-world') {
    return (
      <>
        <LlmMeter />
        <WorldLibraryScreen
          items={libraryItems}
          sort={librarySort}
          legacyWarning={legacyWarning}
          onSort={handleLibrarySort}
          onResume={handleLibraryResume}
          onDelete={handleLibraryDelete}
          onCreateWorld={startCreateWorld}
          onBack={() => setScreen('menu')}
        />
      </>
    );
  }
  if (screen === 'setup') {
    return (
      <>
        <LlmMeter />
        <SetupScreen
          apiKeys={apiKeys}
          provider={provider}
          model={model}
          linger={linger}
          onSave={(p, ks, m, l) => {
            setApiKeys(ks);
            setProvider(p);
            setModel(m);
            setLinger(l);
            savePrefs(p, m, l);
            if (p === 'gemini') void savePersistedKeys(p, ks);
            else void savePersistedKey(p, ks[0] ?? '');
            setScreen('menu');
          }}
          onClearKey={() => {
            setApiKeys([]);
            if (provider === 'gemini') void clearPersistedKeys(provider);
            else void clearPersistedKey(provider);
          }}
          onBack={() => setScreen('menu')}
        />
      </>
    );
  }
  if (screen === 'characters') {
    return (
      <>
        <LlmMeter />
        <CharactersScreen onBack={() => setScreen('menu')} />
      </>
    );
  }
  if (screen === 'cast') {
    return (
      <>
        <LlmMeter />
        <CastScreen
          count={npcCount}
          scenarioId={scenarioId}
          onScenario={(id) => {
            setScenarioId(id);
            try {
              localStorage.setItem('converse:scenario', id);
            } catch {
              // Unwritable storage: selection still applies this session.
            }
          }}
          onBack={() => setScreen('menu')}
          onStart={(ids) => {
            const byId = new Map(loadLibrary().map((e) => [e.npc.id, e]));
            setRoster(ids.map((id) => cloneSavedForWorld(byId.get(id)!)).filter(Boolean));
            setScreen('game');
          }}
        />
      </>
    );
  }
  if (screen === 'game') {
    return (
      <>
        <LlmMeter />
        <GameScreen
          provider={provider}
          apiKeys={apiKeys}
          model={model}
          count={npcCount}
          roster={roster}
          lingerMult={linger}
          scenario={scenarioById(scenarioId)}
          onLeave={() => {
            setScreen('menu');
          }}
          onReturnToCharacterCreation={openAddCharacter}
        />
      </>
    );
  }
  const hasKey = isHostedSite() || apiKeys.some((k) => k.trim().length > 0);
  return (
    <>
      <LlmMeter />
      <HomeShell
        hasKey={ready && hasKey}
        ready={ready}
        onCreateWorld={startCreateWorld}
        onLoadWorld={openLoadWorld}
        onSetup={() => setScreen('setup')}
      />
    </>
  );
}

export { resetWorldLibrary };
